import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { FileSpreadsheet, ArrowLeft, Upload, FileText, CheckCircle, AlertCircle, Loader2, FileJson, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
// import { getAuthToken } from "@/auth";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Função para registrar logs (desabilitada temporariamente)
const logToServer = async (message, type = 'info', data = null) => {
  // Log apenas no console por enquanto
  console.log(`[LOG] ${type.toUpperCase()}: ${message}`, data);
  
  // Desabilitado temporariamente para evitar erros 404
  // const response = await fetch('/api/log', { ... });
};

export default function GoogleSheetsQuestor() {
  const [file, setFile] = useState(null);
  const [date, setDate] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [processingStats, setProcessingStats] = useState(null);
  const [logDetails, setLogDetails] = useState([]);
  // const [processId, setProcessId] = useState(null); // ID único para cada processamento
  const [isDragOver, setIsDragOver] = useState(false); // Estado para drag and drop
  const [chartOfAccounts, setChartOfAccounts] = useState({}); // Plano de contas
  const [balanceReport, setBalanceReport] = useState(null); // Relatório de balanço
  const [pdfUrl, setPdfUrl] = useState(null); // URL do PDF gerado
  const fileInputRef = useRef(null);

  const steps = [
    "Enviando arquivo...",
    "Processando estrutura...",
    "Verificando contas contábeis...",
    "Gerando balanço...",
    "Criando relatório PDF...",
    "Preparando download..."
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError(null);
      // Limpar dados anteriores para forçar reprocessamento
      setDownloadUrl(null);
      setProcessingStats(null);
      setLogDetails([]);
      
      logToServer(`Arquivo selecionado: ${selectedFile.name}`, 'info', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        timestamp: Date.now() // Timestamp para evitar cache
      });
    } else {
      setFile(null);
      setError("Por favor, selecione um arquivo CSV válido.");
      logToServer("Tentativa de selecionar arquivo inválido", 'error');
    }
  };

  // Funções para drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setError(null);
        // Limpar dados anteriores para forçar reprocessamento
        setDownloadUrl(null);
        setProcessingStats(null);
        setLogDetails([]);
        
        logToServer(`Arquivo arrastado: ${droppedFile.name}`, 'info', {
          fileName: droppedFile.name,
          fileSize: droppedFile.size,
          fileType: droppedFile.type,
          timestamp: Date.now()
        });
      } else {
        setError("Por favor, arraste um arquivo CSV válido.");
        logToServer("Tentativa de arrastar arquivo inválido", 'error');
      }
    }
  };

  const handleDateChange = (e) => {
    let inputDate = e.target.value;
    
    // Remove caracteres não numéricos
    const numbersOnly = inputDate.replace(/\D/g, '');
    
    // Formata automaticamente a data quando tiver 8 dígitos
    if (numbersOnly.length === 8) {
      const day = numbersOnly.substring(0, 2);
      const month = numbersOnly.substring(2, 4);
      const year = numbersOnly.substring(4, 8);
      inputDate = `${day}/${month}/${year}`;
    } 
    // Formata parcialmente enquanto digita
    else if (numbersOnly.length > 0) {
      // Adiciona a primeira barra após digitar 2 números (dia)
      if (numbersOnly.length >= 2) {
        const day = numbersOnly.substring(0, 2);
        const rest = numbersOnly.substring(2);
        inputDate = `${day}${rest.length > 0 ? '/' : ''}${rest}`;
      }
      
      // Adiciona a segunda barra após digitar 4 números (dia e mês)
      if (numbersOnly.length >= 4) {
        const day = numbersOnly.substring(0, 2);
        const month = numbersOnly.substring(2, 4);
        const rest = numbersOnly.substring(4);
        inputDate = `${day}/${month}${rest.length > 0 ? '/' : ''}${rest}`;
      }
    }
    
    setDate(inputDate);
    
    // Validação do formato final
    if (inputDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(inputDate)) {
      setError(null); // Limpar erro enquanto digita
    }
  };
  
  const validateForm = () => {
    if (!file) {
      setError("Por favor, selecione um arquivo CSV.");
      return false;
    }
    
    if (!date) {
      setError("Por favor, insira a data.");
      return false;
    }
    
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
      setError("Por favor, insira a data no formato DD/MM/AAAA.");
      return false;
    }
    
    return true;
  };

  // Função para processar o CSV localmente (SEMPRE REPROCESSA - SEM CACHE)
  const processCSVLocally = async (csvContent, dateFormatted, processId) => {
    try {
      // Log para garantir que sempre reprocessa
      await logToServer('FORCANDO_REPROCESSAMENTO', 'info', {
        processId,
        timestamp: Date.now(),
        contentHash: btoa(csvContent.substring(0, 100)), // Hash do início do conteúdo
        forceReprocess: true
      });
      
      const lines = csvContent.split('\n').map(line => line.trim()).filter(line => line);
      const processedEntries = [];
      let debitEntry = null;
      
      // Adicionar timestamp único a cada entrada para evitar qualquer cache
      const uniqueTimestamp = Date.now();
      
      await logToServer('LINHAS_ENCONTRADAS', 'info', {
        totalLines: lines.length,
        firstLines: lines.slice(0, 5)
      });
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Pular linhas de cabeçalho de seção
        if (line.startsWith('RESULTADO,') || line.startsWith('PASSIVO,') || line.startsWith('ATIVO,')) {
          continue;
        }
        
        // Processar linhas de débito e crédito
        const parts = line.split(',');
        if (parts.length >= 8) {
          const type = parts[0];
          const description = parts[1];
          const accountCode = parts[5];
          const value = parts[7];
          
          // Verificar se tem dados válidos
          if (!accountCode || !value || accountCode.trim() === '' || value.trim() === '') {
            continue;
          }
          
          if (type === 'D-') {
            // Extrair descrição limpa (remover código da conta e pontos)
            let cleanDescription = description;
            if (cleanDescription) {
              // Remover códigos numéricos do início e pontos
              cleanDescription = cleanDescription.replace(/^[\d.\s]+\./g, '').trim();
              // Remover caracteres especiais no início
              cleanDescription = cleanDescription.replace(/^[\s.-]+/g, '').trim();
            }
            
            debitEntry = {
              debitCode: accountCode.trim(),
              description: cleanDescription || 'Lançamento contábil',
              value: parseFloat(value.replace(',', '.')) || 0,
              processTimestamp: uniqueTimestamp // Timestamp único para evitar cache
            };
          } else if (type === 'C-' && debitEntry) {
            // Criar entrada no CSV final
            const creditCode = accountCode.trim();
            
            processedEntries.push({
              date: dateFormatted,
              debitCode: debitEntry.debitCode,
              creditCode: creditCode,
              value: debitEntry.value,
              zero: '0',
              description: debitEntry.description,
              processId: processId, // ID único do processamento
              processTimestamp: uniqueTimestamp // Timestamp único
            });
            
            debitEntry = null; // Reset para próximo par
          }
        }
      }
      
      await logToServer('PROCESSAMENTO_FINALIZADO_SEM_CACHE', 'info', {
        processId,
        totalEntries: processedEntries.length,
        uniqueTimestamp,
        forceReprocess: true,
        sampleEntries: processedEntries.slice(0, 3)
      });
      
      return processedEntries;
    } catch (error) {
      throw new Error(`Erro ao processar CSV: ${error.message}`);
    }
  };

  // Função para gerar o CSV final
  const generateFinalCSV = (entries) => {
    const csvLines = entries.map(entry => 
      `${entry.date},${entry.debitCode},${entry.creditCode},${entry.value},${entry.zero},${entry.description}`
    );
    
    return csvLines.join('\n');
  };

  // Função para carregar e processar o plano de contas
  const loadChartOfAccounts = useCallback(async () => {
    try {
      const response = await fetch('/files/DataGrid.csv');
      if (!response.ok) {
        // Se não conseguir carregar, usar plano básico
        console.warn('Não foi possível carregar DataGrid.csv, usando plano básico');
        const basicAccounts = createBasicChartOfAccounts();
        setChartOfAccounts(basicAccounts);
        return basicAccounts;
      }
      
      const csvText = await response.text();
      const lines = csvText.split('\n').filter(line => line.trim());
      const accounts = {};
      
      // Pular a primeira linha (cabeçalho)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const parts = line.split(';');
        
        if (parts.length >= 2) {
          const accountCode = parts[0];
          const description = parts[1];
          
          accounts[accountCode] = {
            code: accountCode,
            description: description.trim(),
            level: getAccountLevel(description)
          };
        }
      }
      
      setChartOfAccounts(accounts);
      return accounts;
    } catch (error) {
      console.warn('Erro ao carregar plano de contas, usando plano básico:', error);
      const basicAccounts = createBasicChartOfAccounts();
      setChartOfAccounts(basicAccounts);
      return basicAccounts;
    }
  }, []);

  // Função para criar plano de contas básico quando não conseguir carregar o arquivo
  const createBasicChartOfAccounts = () => {
    const basicAccounts = {
      // Contas mais comuns baseadas no exemplo
      '7': { code: '7', description: 'Bancos Conta Movimento - No País', level: 4 },
      '51': { code: '51', description: 'Aplicação em CDB de Liquidez Imediada', level: 4 },
      '142': { code: '142', description: 'Clientes Nacionais Diversos', level: 4 },
      '288': { code: '288', description: 'Adiantamentos a Fornecedores', level: 4 },
      '389': { code: '389', description: 'ISS a Recuperar', level: 4 },
      '522': { code: '522', description: 'Construções em Andamento de Imóveis Dest.a Venda', level: 4 },
      '567': { code: '567', description: 'Assinaturas e Anuidades a Apropriar', level: 4 },
      '1083': { code: '1083', description: 'Maquinas Aparelhos e Equipamentos', level: 4 },
      '1089': { code: '1089', description: 'Veículos', level: 4 },
      '1147': { code: '1147', description: '(-) Deprec. Máquinas, Aparelhos e Equipamentos', level: 4 },
      '1153': { code: '1153', description: '(-) Deprec. Veículos', level: 4 },
      '1393': { code: '1393', description: 'Financiamento a Curto Prazo', level: 4 },
      '1496': { code: '1496', description: 'Fornecedores Diversos', level: 4 },
      '1545': { code: '1545', description: 'IRPJ a Recolher', level: 4 },
      '1547': { code: '1547', description: 'ISS a Recolher', level: 4 },
      '1552': { code: '1552', description: 'COFINS a Recolher', level: 4 },
      '1553': { code: '1553', description: 'CSLL a Recolher', level: 4 },
      '1556': { code: '1556', description: 'PIS a Recolher', level: 4 },
      '1634': { code: '1634', description: 'Salários e Ordenados a Pagar', level: 4 },
      '1635': { code: '1635', description: 'Pró Labore a Pagar', level: 4 },
      '1642': { code: '1642', description: 'Gratificações a Pagar', level: 4 },
      '1659': { code: '1659', description: 'INSS a Recolher', level: 4 },
      '1735': { code: '1735', description: 'Água e Esgoto a Pagar', level: 4 },
      '1739': { code: '1739', description: 'Energia Elétrica a Pagar', level: 4 },
      '1741': { code: '1741', description: 'Fretes a Pagar', level: 4 },
      '1742': { code: '1742', description: 'Honorários Contábeis a Pagar', level: 4 },
      '1744': { code: '1744', description: 'Seguros a Pagar', level: 4 },
      '1746': { code: '1746', description: 'Serviços Terceirizados a Pagar', level: 4 },
      '1747': { code: '1747', description: 'Telefones e Internet a Pagar', level: 4 },
      '1963': { code: '1963', description: 'Financiamento a Longo Prazo', level: 4 },
      '2348': { code: '2348', description: 'Capital Social', level: 4 },
      '2703': { code: '2703', description: 'Prestação de Serviços a Prazo', level: 4 },
      '2776': { code: '2776', description: '(-) De Prestação de Serviços Mercado Interno', level: 4 },
      '2828': { code: '2828', description: '(-) ISS', level: 4 },
      '2829': { code: '2829', description: '(-) PIS', level: 4 },
      '2830': { code: '2830', description: '(-) COFINS', level: 4 },
      '3031': { code: '3031', description: 'Custo das Mercadorias Vendidas', level: 4 },
      '4014': { code: '4014', description: 'Salários e Ordenados', level: 4 },
      '4015': { code: '4015', description: 'Pró Labore', level: 4 },
      '4020': { code: '4020', description: 'INSS', level: 4 },
      '4062': { code: '4062', description: 'Comissões Sobre Vendas', level: 4 },
      '4087': { code: '4087', description: 'Anúncios', level: 4 },
      '4161': { code: '4161', description: 'Depreciações', level: 4 },
      '4184': { code: '4184', description: 'Água e Esgoto', level: 4 },
      '4185': { code: '4185', description: 'Energia Elétrica', level: 4 },
      '4186': { code: '4186', description: 'Telecomunicações', level: 4 },
      '4192': { code: '4192', description: 'Serviços Profissionais PJ', level: 4 },
      '4243': { code: '4243', description: 'Revistas e Publicações', level: 4 },
      '4244': { code: '4244', description: 'Legais e Judiciais', level: 4 },
      '4246': { code: '4246', description: 'Honorários Contábeis', level: 4 },
      '4253': { code: '4253', description: 'Fretes e Carretos', level: 4 },
      '4255': { code: '4255', description: 'Segurança e Vigilância', level: 4 },
      '4671': { code: '4671', description: 'IRPJ Lucro Presumido', level: 4 },
      '4672': { code: '4672', description: 'CSSL Lucro Presumido', level: 4 },
      '4698': { code: '4698', description: 'Despesas Bancárias Diversas', level: 4 },
      '4701': { code: '4701', description: 'Juros Pagos ou Incorridos', level: 4 },
      '4856': { code: '4856', description: 'Participações em Empreitadas', level: 4 },
      '4857': { code: '4857', description: 'Taxas Bancárias', level: 4 },
      '4858': { code: '4858', description: 'Publicidade a Pagar', level: 4 },
      '4859': { code: '4859', description: 'Assessoria Jurídica a Pagar', level: 4 },
      '4860': { code: '4860', description: 'Software a Pagar', level: 4 },
      '4861': { code: '4861', description: 'Vigilância e Segurança Eletrônica a Pagar', level: 4 },
      '4862': { code: '4862', description: 'Outras Despesas a Pagar', level: 4 },
      '4863': { code: '4863', description: 'Seguros Diversos', level: 4 },
      '4864': { code: '4864', description: 'Softwares', level: 4 },
      '4865': { code: '4865', description: 'Internet', level: 4 },
      '4866': { code: '4866', description: 'Outras Despesas Operacionais', level: 4 }
    };
    
    return basicAccounts;
  };

  // Função para determinar o nível da conta baseado na estrutura
  const getAccountLevel = (description) => {
    if (description.startsWith('1     ')) return 1; // Nível 1
    if (description.startsWith('1.1     ')) return 2; // Nível 2
    if (description.startsWith('1.1.01     ')) return 3; // Nível 3
    if (description.includes('  .  ')) return 4; // Nível 4 (contas detalhadas)
    
    // Contar espaços para determinar o nível
    const spaces = description.match(/^\s*/)[0].length;
    return Math.min(Math.floor(spaces / 2) + 1, 5);
  };

  // Função para processar lançamentos contábeis
  const processAccountingEntries = (entries, accounts) => {
    const accountBalances = {};
    const accountingEntries = [];
    
    // Inicializar saldos das contas
    Object.keys(accounts).forEach(code => {
      accountBalances[code] = {
        debit: 0,
        credit: 0,
        balance: 0,
        description: accounts[code].description,
        level: accounts[code].level
      };
    });
    
    // Processar cada lançamento
    entries.forEach((entry, index) => {
      const debitAccount = entry.debitCode;
      const creditAccount = entry.creditCode;
      const value = parseFloat(entry.value) || 0;
      
      // Registrar lançamento contábil
      const accountingEntry = {
        id: index + 1,
        date: entry.date,
        description: entry.description,
        value: value,
        debitAccount: {
          code: debitAccount,
          description: accounts[debitAccount]?.description || 'Conta não encontrada'
        },
        creditAccount: {
          code: creditAccount,
          description: accounts[creditAccount]?.description || 'Conta não encontrada'
        }
      };
      
      accountingEntries.push(accountingEntry);
      
      // Atualizar saldos das contas (partidas dobradas)
      if (accountBalances[debitAccount]) {
        accountBalances[debitAccount].debit += value;
        accountBalances[debitAccount].balance += value;
      }
      
      if (accountBalances[creditAccount]) {
        accountBalances[creditAccount].credit += value;
        accountBalances[creditAccount].balance -= value;
      }
    });
    
    return { accountBalances, accountingEntries };
  };

  // Função para encontrar a hierarquia completa de uma conta fazendo lookup reverso
  const findAccountHierarchy = (accountCode, accounts) => {
    const hierarchy = [];
    const hierarchyNames = new Map();
    
    // Construir todos os níveis possíveis da conta
    const parts = accountCode.split('.');
    const levels = [];
    let current = '';
    
    parts.forEach((part, index) => {
      current += (index > 0 ? '.' : '') + part;
      levels.push(current);
    });
    
    // Para cada nível, procurar no plano de contas
    levels.forEach((level, index) => {
      // Procurar a conta exata no plano
      for (const account of Object.values(accounts)) {
        const description = account.description;
        
        // Extrair o código hierárquico da descrição
        const match = description.match(/^([\d.]+)\s+(.+)/);
        if (match) {
          const hierarchicalCode = match[1];
          const name = match[2].trim();
          
          // Se o código hierárquico corresponde ao nível atual
          if (hierarchicalCode === level) {
            hierarchyNames.set(level, {
              code: hierarchicalCode,
              name: name,
              indent: index
            });
            break;
          }
        }
      }
    });
    
    // Construir a hierarquia final
    levels.forEach(level => {
      const hierarchyItem = hierarchyNames.get(level);
      if (hierarchyItem) {
        hierarchy.push({
          level: hierarchyItem.code,
          name: hierarchyItem.name,
          indent: hierarchyItem.indent
        });
      } else {
        // Se não encontrou, usar o código como fallback
        hierarchy.push({
          level: level,
          name: level,
          indent: levels.indexOf(level)
        });
      }
    });
    
    return hierarchy;
  };

  // Função para classificar conta por tipo baseado no código hierárquico
  const classifyAccountType = (accountCode, accounts) => {
    // Usar o primeiro número do código para classificar
    const firstDigit = accountCode.charAt(0);
    
    // Para contas de classe 2, verificar se é patrimônio líquido
    if (firstDigit === '2') {
      // Verificar se é patrimônio líquido baseado nos padrões comuns
      if (accountCode.startsWith('2.4') || accountCode.startsWith('2.5')) {
        return 'PATRIMONIO';
      }
      
      // Verificar no plano de contas se contém palavras-chave de patrimônio
      const hierarchy = findAccountHierarchy(accountCode, accounts);
      for (const level of hierarchy) {
        if (level.name) {
          const nameLower = level.name.toLowerCase();
          if (nameLower.includes('capital') || 
              nameLower.includes('patrimônio') ||
              nameLower.includes('reserva') ||
              nameLower.includes('lucro') ||
              nameLower.includes('prejuizo')) {
            return 'PATRIMONIO';
          }
        }
      }
      
      return 'PASSIVO';
    }
    
    // Para contas de classe 4, verificar se é receita ou dedução
    if (firstDigit === '4') {
      // 4.1 são receitas, outras 4.x são deduções/despesas
      if (accountCode.startsWith('4.1')) {
        return 'RECEITAS';
      } else {
        return 'CUSTOS_DESPESAS';
      }
    }
    
    // Classificar baseado no primeiro dígito
    switch (firstDigit) {
      case '1': return 'ATIVO';
      case '2': return 'PASSIVO';
      case '3': return 'RECEITAS';
      case '5': 
      case '6': 
      case '7': 
      case '8': return 'CUSTOS_DESPESAS';
      default: return 'OUTROS';
    }
  };


  // Função para gerar relatório de balanço estruturado
  const generateBalanceReport = (accountBalances, accounts) => {
    const report = {
      ATIVO: { total: 0, accounts: [], hierarchyMap: new Map() },
      PASSIVO: { total: 0, accounts: [], hierarchyMap: new Map() },
      PATRIMONIO: { total: 0, accounts: [], hierarchyMap: new Map() },
      RECEITAS: { total: 0, accounts: [], hierarchyMap: new Map() },
      CUSTOS_DESPESAS: { total: 0, accounts: [], hierarchyMap: new Map() }
    };
    
    Object.entries(accountBalances).forEach(([code, balance]) => {
      if (balance.debit === 0 && balance.credit === 0) return; // Pular contas sem movimento
      
      const accountType = classifyAccountType(code, accounts);
      const hierarchy = findAccountHierarchy(code, accounts);
      
      const account = {
        code,
        description: balance.description,
        debit: balance.debit,
        credit: balance.credit,
        balance: balance.balance,
        level: balance.level,
        hierarchy: hierarchy,
        fullDescription: accounts[code]?.description || balance.description
      };
      
      if (report[accountType]) {
        report[accountType].accounts.push(account);
        
        // Somar o saldo (para ativo, somar diretamente; para passivo/patrimônio, valor absoluto)
        if (accountType === 'ATIVO' || accountType === 'CUSTOS_DESPESAS') {
          report[accountType].total += balance.balance;
        } else {
          report[accountType].total += Math.abs(balance.balance);
        }
        
        // Construir mapa de hierarquia
        hierarchy.forEach(h => {
          if (!report[accountType].hierarchyMap.has(h.level)) {
            report[accountType].hierarchyMap.set(h.level, {
              name: h.name,
              indent: h.indent,
              total: 0,
              accounts: []
            });
          }
          
          const hierarchyItem = report[accountType].hierarchyMap.get(h.level);
          hierarchyItem.accounts.push(account);
          
          if (accountType === 'ATIVO' || accountType === 'CUSTOS_DESPESAS') {
            hierarchyItem.total += balance.balance;
          } else {
            hierarchyItem.total += Math.abs(balance.balance);
          }
        });
      }
    });
    
    // Ordenar contas dentro de cada seção
    Object.values(report).forEach(section => {
      section.accounts.sort((a, b) => {
        // Ordenar por hierarquia primeiro, depois por código
        const aHierarchy = a.hierarchy.length > 0 ? a.hierarchy[0].level : '999';
        const bHierarchy = b.hierarchy.length > 0 ? b.hierarchy[0].level : '999';
        
        if (aHierarchy !== bHierarchy) {
          return aHierarchy.localeCompare(bHierarchy, undefined, { numeric: true });
        }
        
        return a.code.localeCompare(b.code, undefined, { numeric: true });
      });
    });
    
    return report;
  };

  // Função para gerar PDF do balanço
  const generateBalancePDF = (balanceReport, accountingEntries, fileName) => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFontSize(16);
    doc.text('BALANÇO PATRIMONIAL E DEMONSTRAÇÃO DE RESULTADOS', 20, 20);
    doc.setFontSize(12);
    doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, 30);
    doc.text(`Arquivo: ${fileName}`, 20, 40);
    
    let yPosition = 50;
    
    // Função para adicionar seção com hierarquia
    const addHierarchicalSection = (sectionTitle, sectionData) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Título da seção
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(sectionTitle, 20, yPosition);
      yPosition += 15;
      
      if (sectionData.accounts.length === 0) return;
      
      // Organizar por hierarquia
      const hierarchyLevels = new Map();
      
      sectionData.accounts.forEach(account => {
        account.hierarchy.forEach(h => {
          if (!hierarchyLevels.has(h.level)) {
            hierarchyLevels.set(h.level, {
              name: h.name,
              indent: h.indent,
              accounts: [],
              total: 0
            });
          }
          hierarchyLevels.get(h.level).accounts.push(account);
        });
        
        // Se não tem hierarquia, adicionar diretamente
        if (account.hierarchy.length === 0) {
          const directLevel = `${account.code.charAt(0)}`;
          if (!hierarchyLevels.has(directLevel)) {
            hierarchyLevels.set(directLevel, {
              name: sectionTitle,
              indent: 0,
              accounts: [],
              total: 0
            });
          }
          hierarchyLevels.get(directLevel).accounts.push(account);
        }
      });
      
      // Ordenar níveis hierarquicamente
      const sortedLevels = Array.from(hierarchyLevels.entries())
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
      
      // Gerar conteúdo hierarquicamente
      const tableData = [];
      const processedAccounts = new Set();
      
      sortedLevels.forEach(([level, data]) => {
        const indent = '  '.repeat(data.indent);
        
        // Adicionar cabeçalho do nível se tem nome
        if (data.name && data.name !== level) {
          tableData.push([
            level,
            `${indent}${data.name}`,
            '',
            '',
            ''
          ]);
        }
        
        // Adicionar contas únicas deste nível
        const uniqueAccounts = data.accounts.filter(account => {
          if (processedAccounts.has(account.code)) return false;
          processedAccounts.add(account.code);
          return true;
        });
        
        uniqueAccounts.forEach(account => {
          tableData.push([
            account.code,
            `${indent}  ${account.fullDescription.replace(/^[\d.\s]+/, '').trim()}`,
            account.debit.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
            account.credit.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
            account.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          ]);
        });
      });
      
      if (tableData.length > 0) {
        autoTable(doc, {
          startY: yPosition,
          head: [['Código', 'Descrição', 'Débito (R$)', 'Crédito (R$)', 'Saldo (R$)']],
          body: tableData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: {
            fillColor: [240, 240, 240],
            fontSize: 9,
            fontStyle: 'bold'
          },
          margin: { left: 20, right: 20 },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 80 },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 25, halign: 'right' }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 5;
      }
      
      // Total da seção
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`TOTAL ${sectionTitle}: R$ ${sectionData.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 20, yPosition);
      doc.setFont(undefined, 'normal');
      yPosition += 20;
    };
    
    // Adicionar seções do balanço com hierarquia
    if (balanceReport.ATIVO && balanceReport.ATIVO.accounts.length > 0) {
      addHierarchicalSection('ATIVO', balanceReport.ATIVO);
    }
    
    if (balanceReport.PASSIVO && balanceReport.PASSIVO.accounts.length > 0) {
      addHierarchicalSection('PASSIVO', balanceReport.PASSIVO);
    }
    
    if (balanceReport.PATRIMONIO && balanceReport.PATRIMONIO.accounts.length > 0) {
      addHierarchicalSection('PATRIMÔNIO LÍQUIDO', balanceReport.PATRIMONIO);
    }
    
    if (balanceReport.RECEITAS && balanceReport.RECEITAS.accounts.length > 0) {
      addHierarchicalSection('RECEITAS', balanceReport.RECEITAS);
    }
    
    if (balanceReport.CUSTOS_DESPESAS && balanceReport.CUSTOS_DESPESAS.accounts.length > 0) {
      addHierarchicalSection('CUSTOS E DESPESAS', balanceReport.CUSTOS_DESPESAS);
    }
    
    // Página de lançamentos contábeis
    doc.addPage();
    yPosition = 20;
    
    doc.setFontSize(14);
    doc.text('LANÇAMENTOS CONTÁBEIS', 20, yPosition);
    yPosition += 10;
    
    const entriesData = accountingEntries.slice(0, 50).map(entry => [
      entry.id.toString(),
      entry.date,
      entry.description.substring(0, 30),
      `${entry.debitAccount.code} - ${entry.debitAccount.description.substring(0, 20)}`,
      `${entry.creditAccount.code} - ${entry.creditAccount.description.substring(0, 20)}`,
      entry.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    ]);
    
    if (entriesData.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Data', 'Descrição', 'Conta Débito', 'Conta Crédito', 'Valor (R$)']],
        body: entriesData,
        theme: 'grid',
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });
    }
    
    if (accountingEntries.length > 50) {
      yPosition = doc.lastAutoTable.finalY + 10;
      doc.text(`... e mais ${accountingEntries.length - 50} lançamentos`, 20, yPosition);
    }
    
    // Salvar PDF
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    return pdfUrl;
  };

  // Carregar plano de contas ao montar o componente
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        await loadChartOfAccounts();
      } catch (error) {
        console.warn('Não foi possível carregar o plano de contas:', error);
      }
    };
    loadAccounts();
  }, [loadChartOfAccounts]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setProcessing(true);
    setProgress(0);
    setCurrentStep(0);
    setError(null);
    setDownloadUrl(null);
    setProcessingStats(null);
    setLogDetails([]);

    try {
      // Gerar ID único para este processamento (ANTI-CACHE)
      const uniqueProcessId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Converter data para formato DDMMAAAA
      const [day, month, year] = date.split('/');
      const dateFormatted = `${day}${month}${year}`;
      
      addLogDetail('upload', 'info', 'Iniciando processamento local do arquivo (SEM CACHE)', {
        fileName: file.name,
        fileSize: file.size,
        date: dateFormatted,
        processId: uniqueProcessId,
        forceReprocess: true
      });
      
      await logToServer('INICIO_PROCESSAMENTO_LOCAL_SEM_CACHE', 'info', {
        fileName: file.name,
        fileSize: file.size,
        date: dateFormatted,
        processId: uniqueProcessId,
        timestamp: Date.now(),
        forceReprocess: true
      });

      // Passo 1: Ler arquivo CSV
      setCurrentStep(0);
      setProgress(25);
      
      addLogDetail('upload', 'info', 'Lendo conteúdo do arquivo CSV');
      
      const csvContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Erro ao ler arquivo'));
        reader.readAsText(file);
      });
      
      await logToServer('ARQUIVO_LIDO_SEM_CACHE', 'info', {
        contentLength: csvContent.length,
        firstLines: csvContent.split('\n').slice(0, 5),
        processId: uniqueProcessId,
        timestamp: Date.now(),
        forceReprocess: true
      });
      
      // Passo 2: Processar estrutura (SEMPRE REPROCESSA)
      setCurrentStep(1);
      setProgress(50);
      
      addLogDetail('process', 'info', 'Processando estrutura do arquivo (FORÇANDO REPROCESSAMENTO)');
      
      const processedEntries = await processCSVLocally(csvContent, dateFormatted, uniqueProcessId);
      
      await logToServer('ESTRUTURA_PROCESSADA', 'info', {
        totalEntries: processedEntries.length,
        sampleEntries: processedEntries.slice(0, 3)
      });
      
      addLogDetail('process', 'success', 'Estrutura processada com sucesso', {
        recordsProcessed: processedEntries.length
      });
      
      setProgress(75);
      
      // Passo 3: Verificar contas contábeis
      setCurrentStep(2);
      setProgress(60);
      
      addLogDetail('verify', 'info', 'Verificando contas contábeis no plano de contas');
      
      let accounts = chartOfAccounts;
      if (Object.keys(accounts).length === 0) {
        addLogDetail('verify', 'info', 'Carregando plano de contas...');
        accounts = await loadChartOfAccounts();
      }
      
      // Processar lançamentos contábeis
      const { accountBalances, accountingEntries } = processAccountingEntries(processedEntries, accounts);
      
      await logToServer('CONTAS_VERIFICADAS', 'info', {
        totalAccounts: Object.keys(accounts).length,
        accountingEntries: accountingEntries.length
      });
      
      // Passo 4: Gerar balanço
      setCurrentStep(3);
      setProgress(75);
      
      addLogDetail('balance', 'info', 'Gerando relatório de balanço estruturado');
      
      const balanceReport = generateBalanceReport(accountBalances, accounts);
      setBalanceReport(balanceReport);
      
      // Passo 5: Criar relatório PDF
      setCurrentStep(4);
      setProgress(85);
      
      addLogDetail('pdf', 'info', 'Gerando relatório PDF do balanço');
      
      const originalName = file.name.replace('.csv', '');
      const pdfUrl = generateBalancePDF(balanceReport, accountingEntries, `${originalName} QUESTOR.csv`);
      setPdfUrl(pdfUrl);
      
      // Passo 6: Gerar CSV final e preparar download
      setCurrentStep(5);
      setProgress(95);
      
      addLogDetail('generate', 'info', 'Gerando CSV final');
      
      const finalCSV = generateFinalCSV(processedEntries);
      
      await logToServer('CSV_GERADO', 'info', {
        csvLength: finalCSV.length,
        firstLines: finalCSV.split('\n').slice(0, 5)
      });
      
      // Preparar download
      setProgress(100);
      
      addLogDetail('download', 'success', 'CSV e relatório PDF gerados com sucesso');
      
      // Criar blob e URL de download
      const blob = new Blob([finalCSV], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      // Gerar nome do arquivo baseado no arquivo original
      const downloadFileName = `${originalName} QUESTOR.csv`;
      
      // Fazer download automático
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setDownloadUrl(url);
      
      // Configurar estatísticas
      setProcessingStats({
        recordsProcessed: processedEntries.length,
        fileSize: file.size,
        processingTime: 'Processamento local'
      });
      
      await logToServer('PROCESSAMENTO_CONCLUIDO_SEM_CACHE', 'success', {
        totalEntries: processedEntries.length,
        fileName: downloadFileName,
        processId: uniqueProcessId,
        timestamp: Date.now(),
        forceReprocess: true,
        stats: {
          recordsProcessed: processedEntries.length,
          fileSize: file.size,
          processingTime: 'Processamento local (sem cache)'
        }
      });
      
    } catch (err) {
      console.error('Erro durante o processamento:', err);
      setError(`Erro: ${err.message}`);
      logToServer(`Erro durante o processamento: ${err.message}`, 'error', {
        stack: err.stack
      });
      
      addLogDetail('error', 'error', `Falha no processamento: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  
  const addLogDetail = (type, status, message, data = null) => {
    setLogDetails(prev => [...prev, {
      type,
      status,
      message,
      data,
      timestamp: new Date().toISOString()
    }]);
  };

  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">Google Sheets For Questor</h1>
          </div>
        </div>
        <p className="text-gray-500">
          Converta seus arquivos CSV do Questor para planilhas Excel formatadas
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Conversor de Arquivos</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-6">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </p>
                </div>
              )}

              {downloadUrl ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-6"
                >
                  <div className="text-center mb-6">
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-green-800 mb-2">Processamento Concluído!</h3>
                    <p className="text-green-700 mb-4">
                      Sua planilha foi gerada com sucesso. {processingStats && `${processingStats.recordsProcessed} registros foram processados via ${processingStats.processingTime}.`}
                    </p>
                  </div>

                  {/* Resumo do Balanço */}
                  {balanceReport && (
                    <div className="bg-white rounded-lg p-4 mb-6 border">
                      <h4 className="text-lg font-medium text-gray-800 mb-3 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Resumo do Balanço Contábil
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {balanceReport.ATIVO && balanceReport.ATIVO.total !== 0 && (
                          <div className="text-center p-3 bg-blue-50 rounded">
                            <div className="text-blue-800 font-medium">Ativo</div>
                            <div className="text-blue-600">
                              R$ {balanceReport.ATIVO.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                        {balanceReport.PASSIVO && balanceReport.PASSIVO.total !== 0 && (
                          <div className="text-center p-3 bg-red-50 rounded">
                            <div className="text-red-800 font-medium">Passivo</div>
                            <div className="text-red-600">
                              R$ {balanceReport.PASSIVO.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                        {balanceReport.PATRIMONIO && balanceReport.PATRIMONIO.total !== 0 && (
                          <div className="text-center p-3 bg-purple-50 rounded">
                            <div className="text-purple-800 font-medium">Patrimônio</div>
                            <div className="text-purple-600">
                              R$ {balanceReport.PATRIMONIO.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                        {balanceReport.RECEITAS && balanceReport.RECEITAS.total !== 0 && (
                          <div className="text-center p-3 bg-green-50 rounded">
                            <div className="text-green-800 font-medium">Receitas</div>
                            <div className="text-green-600">
                              R$ {balanceReport.RECEITAS.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                        {balanceReport.CUSTOS_DESPESAS && balanceReport.CUSTOS_DESPESAS.total !== 0 && (
                          <div className="text-center p-3 bg-orange-50 rounded">
                            <div className="text-orange-800 font-medium">Custos/Despesas</div>
                            <div className="text-orange-600">
                              R$ {balanceReport.CUSTOS_DESPESAS.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Botões de Download */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white" 
                      onClick={() => window.open(downloadUrl, '_blank')}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Baixar CSV Questor
                    </Button>
                    
                    {pdfUrl && (
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700 text-white" 
                        onClick={() => window.open(pdfUrl, '_blank')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Baixar Relatório PDF
                      </Button>
                    )}
                  </div>
                  
                  <div className="mt-4 text-sm text-green-600 text-center">
                    <p>✓ CSV para importação no Questor gerado</p>
                    {pdfUrl && <p>✓ Relatório PDF com balanço contábil gerado</p>}
                    <p>✓ Verificação de partidas dobradas concluída</p>
                  </div>
                </motion.div>
              ) : (
                <div className="space-y-6">
                  {/* Área de Upload com Drag and Drop */}
                  <div>
                    <Label className="block mb-4 text-lg font-medium">
                      Arquivo CSV do Questor
                    </Label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                        isDragOver 
                          ? 'border-green-500 bg-green-50' 
                          : file
                          ? 'border-green-400 bg-green-50'
                          : 'border-gray-300 hover:border-green-400'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="flex flex-col items-center gap-4">
                        <div className={`p-4 rounded-full ${
                          file ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          {file ? (
                            <FileText className="w-8 h-8 text-green-600" />
                          ) : (
                            <Upload className="w-8 h-8 text-gray-600" />
                          )}
                        </div>
                        <div>
                          {file ? (
                            <div>
                              <p className="text-lg font-medium text-green-900">
                                {file.name}
                              </p>
                              <p className="text-green-600 mt-1">
                                Arquivo carregado com sucesso
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-lg font-medium text-gray-900">
                                {processing ? 'Processando arquivo...' : 'Arraste e solte o arquivo CSV aqui'}
                              </p>
                              <p className="text-gray-500 mt-1">
                                ou clique para selecionar o arquivo
                              </p>
                            </div>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          className="flex items-center gap-2"
                          disabled={processing}
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          {file ? 'Trocar Arquivo' : 'Selecionar Arquivo'}
                        </Button>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={processing}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Apenas arquivos CSV gerados pelo sistema Questor são suportados.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="date-input" className="block mb-2">
                      Data de Referência
                    </Label>
                    <Input
                      id="date-input"
                      placeholder="DD/MM/AAAA"
                      value={date}
                      onChange={handleDateChange}
                      disabled={processing}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Insira a data de referência para os lançamentos contábeis.
                    </p>
                  </div>

                  {processing ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
                        <h3 className="text-lg font-medium text-blue-800 mb-2">Processando...</h3>
                        <p className="text-blue-700 mb-4">
                          {steps[currentStep]}
                        </p>
                        <div className="w-full max-w-xs mb-2">
                          <Progress value={progress} className="h-2" />
                        </div>
                        <p className="text-xs text-blue-600">
                          Passo {currentStep + 1} de {steps.length}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      onClick={handleSubmit} 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={!file || !date || processing}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Gerar Planilha
                    </Button>
                  )}
                </div>
              )}
              
              {/* Log de Processamento */}
              {logDetails.length > 0 && (
                <div className="mt-8 border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <FileJson className="h-4 w-4" />
                      Log de Processamento
                    </h3>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto text-sm">
                    {logDetails.map((log, index) => (
                      <div 
                        key={index} 
                        className={`p-2 rounded ${
                          log.status === 'success' ? 'bg-green-50 text-green-700' : 
                          log.status === 'error' ? 'bg-red-50 text-red-700' : 
                          'bg-blue-50 text-blue-700'
                        }`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">{log.message}</span>
                          <span className="text-xs opacity-70">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {log.data && (
                          <div className="text-xs mt-1 opacity-80">
                            {Object.entries(log.data).map(([key, value]) => (
                              <div key={key} className="flex">
                                <span className="font-medium mr-1">{key}:</span>
                                <span>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1">O que é isso?</h3>
                  <p className="text-sm text-gray-600">
                    Esta ferramenta converte arquivos CSV exportados do Questor, realiza verificações contábeis baseadas no plano de contas e gera relatórios completos em PDF.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium mb-1">Funcionalidades</h3>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc pl-4">
                    <li>Conversão de CSV para formato Questor</li>
                    <li>Verificação de contas contábeis</li>
                    <li>Aplicação do método das partidas dobradas</li>
                    <li>Geração de balanço patrimonial estruturado</li>
                    <li>Relatório PDF com demonstrações contábeis</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium mb-1">Como usar</h3>
                  <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-4">
                    <li>Exporte um arquivo CSV do seu sistema Questor</li>
                    <li>Faça upload do arquivo CSV</li>
                    <li>Insira a data de referência no formato DD/MM/AAAA</li>
                    <li>Clique em "Gerar Planilha" e aguarde o processamento</li>
                    <li>Baixe o CSV convertido e o relatório PDF</li>
                    <li>Importe o CSV no Questor com confiança</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-sm">
                  <p>
                    <strong>Dica:</strong> Certifique-se de que o arquivo CSV exportado do Questor contém todos os campos necessários para a conversão correta dos dados.
                  </p>
                </div>
                
                {/* Seção de Logs */}
                <div>
                  <h3 className="font-medium mb-1">Logs do Sistema</h3>
                  <p className="text-sm text-gray-600">
                    Todos os processamentos são registrados em arquivos de log na pasta <code>/logs</code> do servidor para facilitar a depuração de problemas.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Os logs incluem detalhes completos de todas as requisições e respostas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
